import React, { type JSX } from 'react';
import { Route, Routes } from 'react-router';
import ModalComponent from './modal';
import { withRouter } from '../hooks/withRouter';

/**
 * Props accepted by the underlying `Modal` component (defined in modal.jsx).
 *
 * `modal.jsx` documents its props via JSDoc `@param` tags (e.g. `{Component}`,
 * `{Children}`, `{Function}`). Because this repo has `checkJs` enabled, TypeScript
 * parses those JSDoc type names literally and infers a prop type where every
 * property is required. In reality all of Modal's props are optional (it has
 * runtime defaults), so we declare the accurate shape here and cast the import
 * once, instead of scattering `as any` at every call site.
 */
interface ModalProps {
  stackOrder?: number;
  children?: React.ReactNode;
  component?: React.ComponentType<any>;
  props?: Record<string, unknown>;
  onBackdropClick?: () => void;
  className?: string;
  inClassName?: string;
  outClassName?: string;
  backdropClassName?: string;
  backdropInClassName?: string;
  backdropOutClassName?: string;
  outDelay?: number;
}

const Modal = ModalComponent as unknown as React.ComponentType<ModalProps>;

/**
 * Represents a location in the routing system with pathname information.
 */
export interface Location {
  pathname: string;
  search?: string;
  hash?: string;
  state?: Record<string, unknown>;
}

/**
 * Represents route parameter matching, supporting both string keys and numeric indices.
 * Numeric indices (e.g., params[0]) capture captured groups from wildcard routes.
 */
export interface RouteParams {
  [key: string | number]: unknown;
}

/**
 * Represents the matched route state.
 */
export interface RouteMatch {
  url: string;
  params: RouteParams;
}

/**
 * Represents the navigation history interface with minimal required methods.
 */
export interface NavigationHistory {
  push: (path: string) => void;
}

/**
 * Props for components rendered inside ModalRoute.
 * These are passed automatically by the ModalRoute component.
 */
interface ModalComponentProps {
  match: RouteMatch;
  location: Location;
  history: NavigationHistory;
  parentPath: string;
  closeModal: () => void;
}

/**
 * Configuration props for the ModalRoute component.
 */
interface ModalRouteProps {
  // ...existing code...
  match: RouteMatch;
  history: NavigationHistory;
  location: Location;
  path: string;
  exact?: boolean;
  onBackdropClick?: () => void;
  children?: React.ReactNode;
  /** React component to render in the modal. Will receive ModalComponentProps. */
  component?: React.ComponentType<Partial<ModalComponentProps> & Record<string, unknown>>;
  /** Additional props to pass to the rendered component */
  props?: Record<string, unknown>;
  className?: string;
  parentPath?: string | ((match: RouteMatch) => string);
  backdropClassName?: string;
  backdropInClassName?: string;
  backdropOutClassName?: string;
  inClassName?: string;
  outClassName?: string;
  outDelay?: number;
}

/**
 * Configuration props to pass to the Modal component (spread from ModalRoute).
 * Excludes route control props that are handled separately.
 */
type ModalPassthroughProps = Omit<
  ModalRouteProps,
  'match' | 'history' | 'location' | 'path' | 'parentPath' | 'onBackdropClick' | 'exact' | 'props'
>;

/**
 * Calculates the z-index stacking order for modals based on the route match.
 * Orders routed modals behind any directly instantiated modals.
 *
 * @param match The route match object containing the URL
 * @returns The stacking order as a number (negative to place behind other modals)
 */
function getStackOrder(match: RouteMatch) {
  return match?.url?.length - 10000;
}

/**
 * A react-router Route component that shows a modal when the location pathname matches.
 *
 * When the route matches, the modal is shown. If multiple routes match, the modals will be
 * stacked based on the length of the path that is matched.
 *
 * The component rendered in the modal will automatically receive these props:
 * - `parentPath`: Either the parentPath specified in the ModalRoute, or a calculated value based on matched url
 * - `closeModal`: A convenience method to close the modal by navigating to the parentPath
 * - `match`: The route match object with URL and params
 * - `location`: The current location with pathname and other routing info
 * - `history`: The navigation history object
 *
 * @param routeProps Modal route configuration with the following properties:
 *   - `path`: Path to match in the route
 *   - `exact`: If set, only show modal if route exactly matches path
 *   - `parentPath`: Path to navigate to when backdrop is clicked, or a function that computes it from route match
 *   - `onBackdropClick`: Handler to invoke when backdrop is clicked. If set, overrides navigation to parentPath
 *   - `className`: Class name to apply to modal container
 *   - `children`: Modal content can be specified as child elements
 *   - `component`: React component to render in the modal (receives ModalComponentProps)
 *   - `props`: Additional props to pass to the rendered component
 *   - `inClassName`: Class name applied to modal when shown (default: react-router-modal__modal--in)
 *   - `outClassName`: Class name applied to modal before hidden (default: react-router-modal__modal--out)
 *   - `backdropClassName`: Class name applied to backdrop (default: react-router-modal__backdrop)
 *   - `backdropInClassName`: Class name applied to backdrop when shown (default: react-router-modal__backdrop--in)
 *   - `backdropOutClassName`: Class name applied to backdrop before hidden (default: react-router-modal__backdrop--out)
 *   - `outDelay`: Delay in milliseconds to wait when closing modal for CSS transitions
 * @returns JSX.Element
 *
 * @example
 * <ModalRoute
 *   path="/user/:id/edit"
 *   parentPath="/users"
 *   component={EditUserForm}
 *   exact
 * />
 */
function ModalRoute(routeProps: ModalRouteProps): JSX.Element {
  const {
    path,
    parentPath,
    props,
    onBackdropClick,
    match,
    history
  } = routeProps;

  // Extract only the props that should be passed to the Modal component
  const modalProps: ModalPassthroughProps = {
    component: routeProps.component,
    className: routeProps.className,
    backdropClassName: routeProps.backdropClassName,
    backdropInClassName: routeProps.backdropInClassName,
    backdropOutClassName: routeProps.backdropOutClassName,
    inClassName: routeProps.inClassName,
    outClassName: routeProps.outClassName,
    outDelay: routeProps.outDelay
  };

  /**
   * Determines the path to navigate to when the modal closes.
   * Priority: function(match) → explicit parentPath → captured param[0] → match.url
   */
  const getParentPath = (match: RouteMatch): string => {
    if (typeof parentPath === 'function') {
      return parentPath(match);
    }
    if (parentPath) {
      return parentPath;
    }
    // Support wildcard route capture: routes like "/parent/(.*)" capture parent path in params[0]
    const capturedPath = match.params[0];
    if (capturedPath !== undefined && capturedPath !== null) {
      return capturedPath === '' ? '/' : (capturedPath as string);
    }
    return match.url;
  };

  return (
    <Routes>
      <Route
        path={path}
        element={
          <Modal
            {...modalProps}
            props={{
              ...props,
              match,
              location: routeProps.location,
              history,
              parentPath: getParentPath(match),
              closeModal: () => history.push(getParentPath(match))
            }}
            stackOrder={getStackOrder(match)}
            onBackdropClick={onBackdropClick || (() => history.push(getParentPath(match)))}
          >
            {routeProps.children ?? null}
          </Modal>
        }
      />
    </Routes>
  );
}

export default withRouter(ModalRoute);
